/* Namespaced localStorage helpers for the mock-data layer.
 *
 * Every mock domain (cart, orders, addresses, …) is stored under
 *   nethrasap.mock.v1.<domain>
 *
 * Writes broadcast a CustomEvent on the same tab and rely on the native
 * `storage` event for cross-tab sync. Consumers subscribe via `onChange`.
 *
 *   import * as store from "@/data/mocks/storage";
 *   store.set("cart", [...]);
 *   const items = store.get("cart", []);
 */

export const NS = "nethrasap.mock.v1.";
export const EVENT = "nethrasap:mock-changed";

function safeKey(domain) {
  return NS + String(domain);
}

export function get(domain, fallback = null) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(safeKey(domain));
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

export function set(domain, value) {
  if (typeof window === "undefined") return value;
  try {
    window.localStorage.setItem(safeKey(domain), JSON.stringify(value));
  } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { domain, value } }));
  } catch (_) {}
  return value;
}

export function update(domain, updater, fallback = null) {
  const current = get(domain, fallback);
  const next = typeof updater === "function" ? updater(current) : updater;
  set(domain, next);
  return next;
}

export function remove(domain) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(safeKey(domain));
  } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { domain, value: null } }));
  } catch (_) {}
}

export function onChange(domain, callback) {
  if (typeof window === "undefined") return () => {};
  const handler = (e) => {
    if (!domain || e?.detail?.domain === domain) {
      callback(e?.detail?.value, e?.detail?.domain);
    }
  };
  const onStorage = (e) => {
    if (e.key && e.key.startsWith(NS)) {
      const dom = e.key.slice(NS.length);
      if (!domain || dom === domain) {
        try { callback(JSON.parse(e.newValue), dom); } catch (_) { callback(null, dom); }
      }
    }
  };
  window.addEventListener(EVENT, handler);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener("storage", onStorage);
  };
}

export function clearAll() {
  if (typeof window === "undefined") return;
  const keys = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(NS)) keys.push(k);
  }
  keys.forEach((k) => {
    try { window.localStorage.removeItem(k); } catch (_) {}
  });
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { domain: "*", value: null } }));
  } catch (_) {}
}

export function dump() {
  if (typeof window === "undefined") return {};
  const out = {};
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(NS)) {
      try { out[k.slice(NS.length)] = JSON.parse(window.localStorage.getItem(k)); }
      catch (_) { out[k.slice(NS.length)] = null; }
    }
  }
  return out;
}
